import { useCallback, useEffect, useRef, useState } from 'react'
import { TranscriptEntry } from './useSpeechRecognition'

// Streaming transcription via a server-side relay (browser → your relay → Deepgram /
// Google). Deliberately the SAME interface as useSpeechRecognition so it's a drop-in:
// the app consumes {text, isFinal, ts, confidence} entries and doesn't care which
// engine produced them. The API key lives in the relay, never the client. No audio is
// retained — the relay pipes PCM straight to the provider (Phase 1b).
//
// Capture: getUserMedia → AudioContext → ScriptProcessor → 16-bit PCM over a WebSocket.
// (ScriptProcessorNode is deprecated but ubiquitous; swap to an AudioWorklet later
// without touching the rest of the app.)

const DEFAULT_RELAY = process.env.REACT_APP_STT_RELAY_URL || 'ws://localhost:8080/stt'

export type UseStreamingOptions = {
  active: boolean
  tsBase: number
  provider?: 'deepgram' | 'google' // which adapter the relay should use
  lang?: string
  relayUrl?: string
  onTranscript: (entry: TranscriptEntry) => void
  onSpeechStart?: () => void
  onSpeechEnd?: () => void
  onPermissionDenied?: () => void
}

export type StreamingState = {
  supported: boolean
  listening: boolean
  error: string | null
  audioSeconds: number // cumulative audio streamed this session (the billing basis)
}

const floatTo16BitPCM = (input: Float32Array): ArrayBuffer => {
  const buf = new ArrayBuffer(input.length * 2)
  const view = new DataView(buf)
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  return buf
}

export const useStreamingTranscription = (opts: UseStreamingOptions): StreamingState => {
  const { active, provider = 'deepgram', lang = 'en-US', relayUrl = DEFAULT_RELAY } = opts

  const supported =
    typeof window !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    !!window.WebSocket &&
    !!(window.AudioContext || (window as any).webkitAudioContext)

  const [listening, setListening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioSeconds, setAudioSeconds] = useState(0)

  // Latest callbacks/values without re-opening the socket each render.
  const cbRef = useRef(opts)
  cbRef.current = opts

  const wsRef = useRef<WebSocket | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const nodesRef = useRef<{ src?: AudioNode; proc?: ScriptProcessorNode; mute?: GainNode }>({})
  const audioSecRef = useRef(0) // accumulates across puzzles (cumulative session total)
  const meterRef = useRef<number | null>(null)

  const stop = useCallback(() => {
    if (meterRef.current) { window.clearInterval(meterRef.current); meterRef.current = null }
    try { nodesRef.current.proc?.disconnect() } catch {}
    try { nodesRef.current.src?.disconnect() } catch {}
    try { nodesRef.current.mute?.disconnect() } catch {}
    try { ctxRef.current?.close() } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()) } catch {}
    // Detach handlers BEFORE closing: close() is async and the provider often flushes a final
    // for the last utterance as the socket closes. Left attached, that late message would fire
    // onmessage after the next puzzle has already started → its transcript bleeds into the next
    // puzzle's trace. Nulling onmessage drops those stragglers so each puzzle stays clean.
    const ws = wsRef.current
    if (ws) {
      ws.onmessage = null
      ws.onopen = null
      ws.onerror = null
      ws.onclose = null
      try { ws.close() } catch {}
    }
    nodesRef.current = {}
    ctxRef.current = null
    streamRef.current = null
    wsRef.current = null
    setListening(false)
  }, [])

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const Ctx = window.AudioContext || (window as any).webkitAudioContext
      const ctx: AudioContext = new Ctx()
      ctxRef.current = ctx
      const sampleRate = Math.round(ctx.sampleRate)

      const url = `${relayUrl}?engine=${provider}&lang=${encodeURIComponent(lang)}&sampleRate=${sampleRate}`
      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onopen = () => {
        setListening(true)
        setError(null)
        const src = ctx.createMediaStreamSource(stream)
        const proc = ctx.createScriptProcessor(4096, 1, 1)
        // Route through a silent gain so the processor runs without echoing to speakers.
        const mute = ctx.createGain()
        mute.gain.value = 0
        proc.onaudioprocess = (ev) => {
          if (ws.readyState !== WebSocket.OPEN) return
          const data = ev.inputBuffer.getChannelData(0)
          audioSecRef.current += data.length / ctx.sampleRate // seconds of audio streamed
          ws.send(floatTo16BitPCM(data))
        }
        src.connect(proc)
        proc.connect(mute)
        mute.connect(ctx.destination)
        nodesRef.current = { src, proc, mute }
        // Surface the running total ~1×/s (low re-render churn) for the debug readout.
        meterRef.current = window.setInterval(() => setAudioSeconds(audioSecRef.current), 1000)
      }

      ws.onmessage = (ev) => {
        let msg: any
        try {
          msg = JSON.parse(ev.data)
        } catch {
          return
        }
        if (msg.type === 'transcript') {
          cbRef.current.onTranscript({
            ts: Math.round(performance.now() - cbRef.current.tsBase),
            text: msg.text || '',
            isFinal: !!msg.isFinal,
            confidence: typeof msg.confidence === 'number' ? msg.confidence : 0,
          })
        } else if (msg.type === 'speech_start') {
          cbRef.current.onSpeechStart?.()
        } else if (msg.type === 'error') {
          setError(msg.message || 'relay error')
        }
      }
      ws.onerror = () => setError('relay connection error')
      ws.onclose = () => setListening(false)
    } catch (e: any) {
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') cbRef.current.onPermissionDenied?.()
      setError(e?.message || 'microphone / relay error')
    }
  }, [provider, lang, relayUrl])

  useEffect(() => {
    if (!supported) return
    if (active) start()
    else stop()
    return () => stop()
  }, [active, supported, start, stop])

  return { supported, listening, error, audioSeconds }
}
